import { Box, Text } from "@chakra-ui/react";
import React, { memo, useMemo } from "react";

import ReportDataTable from "../../reports/ReportDataTable";
import { ReportSectionIntro, ReportTitleWithInfo } from "../../reports/ReportTitleWithInfo";
import ServicesSessionTimeTable, {
  renderAgentServiceCell,
} from "../tables/ServicesSessionTimeTable";
import {
  AGENT_SERVICE_TIME_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
  usesUnifiedTable,
} from "../../../store/renderConfig";
import { getAgentServiceTimeRowsByBranch } from "../../../store/agentServiceTimeExport";
import { resolveAgentServiceBranchTicketCount } from "../../../store/agentServiceTimeMetrics";
import {
  getAgentReviewPriorityRows,
  getAgentServiceTimeInsights,
} from "../../../store/agentServiceTimeInsights";
import { formatTime } from "../../../store/helpers";

const SERVICE_COLUMN = Object.freeze({ id: "serviceName", label: "Service", pinned: true });

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const FLAG_LABELS = Object.freeze({
  "Slow average": "Slow service average",
  "Slow branch average": "Slow branch average",
  "High time / low tickets": "High time / low tickets",
  "Low volume": "Low volume",
  "High workload share": "High workload share",
});

const formatFlagReason = (agent = {}) => {
  const flags = Array.isArray(agent.flags) ? agent.flags : [];
  if (flags.length) {
    return flags.map((flag) => FLAG_LABELS[flag] || flag).join(", ");
  }
  if (agent.effectivenessBand === "Needs review") return "Slower than benchmark";
  return agent.effectivenessBand || "Expected range";
};

const SUMMARY_DESCRIPTION = "Summary is based on completed tickets only. Agents are flagged when their handling time is materially slower than the service or branch benchmark, or when their time share is high compared with their ticket share.";
const AGENT_FLAG_DESCRIPTION = "Prioritized agents are ranked by flagged status, number of flags, variance from branch average, workload balance, and ticket volume. Review Reason shows the actual trigger.";
const SERVICE_FLAG_DESCRIPTION = "Shows services with the highest concentration of flagged agents. No Completed Tickets counts agents in that service group who completed zero tickets in the period.";
const DETAIL_DESCRIPTION = "Full detail by agent and service for comparing ticket counts, total service time, average handling time, and configured columns after the summary has identified the main flags.";

const EnterpriseSummary = ({ branch }) => {
  const { branchSummary, agentSummary, serviceSummary, exceptionSummary } = useMemo(
    () => getAgentServiceTimeInsights(branch),
    [branch]
  );
  const reviewPriorityAgents = getAgentReviewPriorityRows(agentSummary);
  const serviceRisks = [...serviceSummary]
    .sort(
      (left, right) =>
        (right.reviewAgentCount || 0) - (left.reviewAgentCount || 0) ||
        (right.noWorkAgentCount || 0) - (left.noWorkAgentCount || 0)
    )
    .slice(0, 5);

  return (
    <Box className="agentServiceEnterpriseSummary" mb="20px">
      <ReportTitleWithInfo description={SUMMARY_DESCRIPTION}>Agent Effectiveness Summary</ReportTitleWithInfo>
      <Box className="agentServiceKpiGrid">
        <Box><Text>Completed Tickets</Text><strong>{branchSummary.completedTickets}</strong></Box>
        <Box><Text>Total Service Time</Text><strong>{formatTime(branchSummary.totalServiceTime)}</strong></Box>
        <Box><Text>Avg / Ticket</Text><strong>{formatTime(branchSummary.averageServiceTime)}</strong></Box>
        <Box><Text>Active Agents</Text><strong>{branchSummary.activeAgentCount}</strong></Box>
        <Box><Text>Flagged Agents</Text><strong>{branchSummary.reviewAgentCount}</strong></Box>
        <Box><Text>No Completed Tickets</Text><strong>{branchSummary.noWorkAgentCount}</strong></Box>
      </Box>

      <Box className="agentServiceSummaryGrid">
        <Box>
          <ReportTitleWithInfo as="h4" description={AGENT_FLAG_DESCRIPTION}>
            Agent Flag Priority
          </ReportTitleWithInfo>
          <ReportDataTable
            columns={[
              { id: "agentName", label: "Agent" },
              { id: "completedTickets", label: "Tickets" },
              { id: "averageServiceTime", label: "Avg / Ticket" },
              { id: "avgVsBranchAveragePct", label: "Vs Branch Avg" },
              { id: "serviceTimeShare", label: "Time Share" },
              { id: "flagReason", label: "Review Reason" },
            ]}
            rows={reviewPriorityAgents}
            renderConfig={{ columns: ["agentName", "completedTickets", "averageServiceTime", "avgVsBranchAveragePct", "serviceTimeShare", "flagReason"] }}
            renderCell={(row, columnId) => {
              if (columnId === "averageServiceTime") return formatTime(row.averageServiceTime);
              if (columnId === "avgVsBranchAveragePct") return formatPercent(row.avgVsBranchAveragePct);
              if (columnId === "serviceTimeShare") return formatPercent(row.serviceTimeShare);
              if (columnId === "flagReason") return formatFlagReason(row);
              return row[columnId];
            }}
            presortedRows={true}
          />
        </Box>

        <Box>
          <ReportTitleWithInfo as="h4" description={SERVICE_FLAG_DESCRIPTION}>
            Service Flag Summary
          </ReportTitleWithInfo>
          <ReportDataTable
            columns={[
              { id: "serviceName", label: "Service" },
              { id: "completedTickets", label: "Tickets" },
              { id: "activeAgentCount", label: "Active Agents" },
              { id: "reviewAgentCount", label: "Flagged Agents" },
              { id: "noWorkAgentCount", label: "No Completed Tickets" },
            ]}
            rows={serviceRisks}
            renderConfig={{ columns: ["serviceName", "completedTickets", "activeAgentCount", "reviewAgentCount", "noWorkAgentCount"] }}
            renderCell={(row, columnId) => row[columnId]}
            presortedRows={true}
          />
        </Box>
      </Box>

      <Text fontSize="sm" color="gray.600" mt="10px">
        Exceptions: {exceptionSummary.reviewAgents.length} flagged agents, {exceptionSummary.noWorkAgents.length} agents have no completed tickets{branchSummary.unassignedCompletedTickets > 0 ? `, ${branchSummary.unassignedCompletedTickets} tickets were unassigned` : ""}.
      </Text>
    </Box>
  );
};

const UnifiedBranchTable = ({ branchName, servicesData, config, onRenderConfigChange }) => {
  const rows = useMemo(
    () => getAgentServiceTimeRowsByBranch({ branchName, servicesData }, config),
    [branchName, config, servicesData]
  );

  return (
    <Box className="agentServiceTimeUnifiedBlock" mt="16px" mb="24px">
      <ReportDataTable
        pinnedColumns={[SERVICE_COLUMN]}
        columns={AGENT_SERVICE_TIME_COLUMN_DEFINITIONS}
        rows={rows}
        renderConfig={config}
        renderCell={renderAgentServiceCell}
        presortedRows={true}
        onSortChange={(sort) => onRenderConfigChange?.({ ...config, sort })}
      />
    </Box>
  );
};

const AgentServicesSessionTime = ({ data, renderConfig, onRenderConfigChange }) => {
  const config = useMemo(() => normalizeRenderConfig(renderConfig), [renderConfig]);
  const useUnifiedTable = useMemo(() => usesUnifiedTable(config), [config]);

  return (
    <Box>
      {data.map(
        (branch, branchIndex) => {
          const { branchName, servicesData = [], numServices } = branch;
          const title = `${
            branchIndex + 1
          }. ${branchName}`;
          const completedTicketCount = resolveAgentServiceBranchTicketCount(branch);
          
          return (
            <React.Fragment key={branchIndex}>
              <h2>{title}</h2>
              <Text>
                {numServices} services
                {completedTicketCount !== undefined ? ` · ${completedTicketCount} completed tickets` : ""}.
              </Text>
              <Text fontSize="sm" color="gray.600" mb="12px">
                Based on completed tickets only. Share of Service Time is each agent total service time divided by total service time for the service.
              </Text>
              <EnterpriseSummary branch={branch} />
              <ReportSectionIntro title="Detailed Agent Service Time" description={DETAIL_DESCRIPTION} />
              {!useUnifiedTable ? (
                servicesData.map((data, index) => {
                  return (
                    <ServicesSessionTimeTable
                      key={index}
                      index={index}
                      serviceData={data}
                      branchIndex={branchIndex}
                      branchName={branchName}
                      renderConfig={renderConfig}
                      onRenderConfigChange={onRenderConfigChange}
                    />
                  );
                })
              ) : (
                <UnifiedBranchTable
                  branchName={branchName}
                  servicesData={servicesData}
                  config={config}
                  onRenderConfigChange={onRenderConfigChange}
                />
              )}
            </React.Fragment>
          );
        }
      )}
    </Box>
  );
};

export default memo(AgentServicesSessionTime);
